const containerSizes = ['20', '24', '40', '45', '53'];

const containerTypes = [
    { value: 'DS', label: 'Dry Standard' },
    { value: 'DH', label: 'High Cube' },
    { value: 'FS', label: 'Flat Rack' },
    { value: 'FH', label: 'Flat Rack High Cube' },
    { value: 'OS', label: 'Open Top' },
    { value: 'OH', label: 'Open Top High Cube' },
    { value: 'RS', label: 'Reefer' },
    { value: 'RH', label: 'Reefer High Cube' },
];

const containerTypesValues = containerTypes.map((x) => x.value);

const reeferContainerTypesValues = ['RS', 'RH'];

// A customer or terminal (backend BaseBusiness) the container is returned to.
type ContainerReturnLocation = {
    id: string | number;
    name?: string | null;
    shortName?: string | null;
};

function getLocationDisplayName(location?: ContainerReturnLocation | null): string {
    if (!location) return '';
    return location.shortName || location.name || '';
}

// Terminals with a `.container-terminal-*` color class (ContainerModal's global styles).
const coloredTerminalNames = ['ets', 'trapac', 'oict'];

// Customers and other terminals get no color class, so they keep the neutral badge style.
function getTerminalColorClassForLocation(location?: ContainerReturnLocation | null): string {
    const locationName = getLocationDisplayName(location).trim().toLowerCase();
    return coloredTerminalNames.includes(locationName) ? `container-terminal-${locationName}` : '';
}

// returnLocation is only saved when "Return to Another Location?" is checked, so a set value
// already means "different"; the comparison is a safety net. pickupTerminal is the
// container's `terminal` field, which can read "ETS (Expected)" or "N/A". Compared
// case-insensitively, since the TraPac terminal's short name is "TraPac" but the code is "TRAPAC".
function getIsReturningToDifferentLocation(pickupTerminal?: string | null, returnLocation?: ContainerReturnLocation | null): boolean {
    const returnLocationName = getLocationDisplayName(returnLocation).trim().toUpperCase();
    if (!returnLocationName) return false;
    if (!pickupTerminal) return true;
    return pickupTerminal.replace(/ \(Expected\)$/, '').trim().toUpperCase() !== returnLocationName;
}

// sizeType is e.g. "40RH"; an export without a size can come back as just the type ("RH").
function getIsReeferSizeType(sizeType?: string | null): boolean {
    if (!sizeType) return false;
    return reeferContainerTypesValues.includes(sizeType.slice(-2));
}

// Genset temperature/vents apply to every reefer, also when it runs on in-house power.
function getHasGensetTemperatureOrVents(container: any): boolean {
    const hasValue = (value: unknown) => value !== null && value !== undefined;
    return hasValue(container.gensetTemperatureF)
        || hasValue(container.gensetTemperatureC)
        || hasValue(container.gensetVentsOpenPercent);
}

// e.g. "Genset: 34 F, vents 20% open" or "Genset: temp not set, vents not set"
function getGensetTemperatureAndVentsTooltip(container: any): string {
    const hasValue = (value: unknown) => value !== null && value !== undefined;
    const unit = container.gensetTemperatureUnit;
    const temperature = unit === 'F' ? container.gensetTemperatureF : container.gensetTemperatureC;
    const ventsOpenPercent = container.gensetVentsOpenPercent;
    return [
        'Genset: ',
        unit && hasValue(temperature) ? `${temperature} ${unit}, ` : 'temp not set, ',
        hasValue(ventsOpenPercent) ? `vents ${ventsOpenPercent}% open` : 'vents not set',
    ].join('');
}

const containerSizeTypeMixin = {
    data() {
        return {
            containerSizes,
            containerTypes,
            containerTypesValues,
        };
    },
};

const containerSizeTypeStringMixin = {
    methods: {
        containerSizeTypeString(sizeType: string): string {
            switch (sizeType) {
                case '40DS':
                    return "40' Dry Std";
                case '20DS':
                    return "20' Dry Std";
                case '20RS':
                    return "20' Reefer";
                case '20RH':
                    return "20' Reefer High Cube";
                case '40RS':
                    return "40' Reefer";
                case '40RH':
                    return "40' Reefer High Cube";
                case '20DH':
                    return "20' High Cube";
                case '40DH':
                    return "40' High Cube";
                case '20FS':
                    return "20' Flat Rack";
                case '20FH':
                    return "20' Flat Rack High Cube";
                case '40FS':
                    return "40' Flat Rack";
                case '40FH':
                    return "40' Flat Rack High Cube";
                case '40OS':
                    return "40' Open Top";
                case '40OH':
                    return "40' Open Top High Cube";
                default:
                    return '';
            }
        },
    },
};

const terminals = [
    {
        value: 'TRAPAC', label: 'TraPac',
    },
    {
        value: 'OICT', label: 'OICT',
    },
    {
        value: 'ETS', label: 'ETS',
    },
];

// The container isn't off the vessel yet (or we can't tell), so it shows the ETA, not the LFD.
// Same statuses as the backend's CONTAINER_STATUS_CATEGORIES_WITHOUT_LFD.
const yardStatusesShowingEta = ['Unknown', 'NotFound', 'Onboard'];

function getIsEtaShownForYardStatus(yardStatus: string): boolean {
    return yardStatusesShowingEta.includes(yardStatus);
}

export {
    containerSizes, containerTypes, containerTypesValues, terminals, containerSizeTypeMixin, containerSizeTypeStringMixin,
    getIsReeferSizeType, getIsReturningToDifferentLocation, getLocationDisplayName,
    getTerminalColorClassForLocation, getHasGensetTemperatureOrVents,
    getGensetTemperatureAndVentsTooltip, getIsEtaShownForYardStatus,
};

export type { ContainerReturnLocation };
